import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAbility } from '@casl/react';
import { AlertTriangle, Info, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Combobox } from '../../components/ui/combobox';
import { Switch } from '../../components/ui/switch';
import AccessRestricted from '../../components/AccessRestricted';
import { AbilityContext } from '../../context/casl/AbilityContext';
import { ACTIONS, RESOURCES } from '../../constants/permissions.constants';
import {
    DeliveryNotificationEvent,
    DeliveryNotificationSettings,
} from '../../types/stock.types';
import { deliverySettingsApi } from '../../utils/stock-api';
import api from '../../utils/api';

interface PickerOption {
    label: string;
    value: string;
    subtitle?: string;
}

interface AllUser {
    id: number;
    name: string;
    email?: string;
    jobRole?: string;
}

/**
 * The six points in the delivery workflow that notify, mirroring
 * backend/services/StockDeliveryNotificationService.js. Recipients are resolved from the
 * data (see the banner on the page), not chosen here — this list only names the trigger
 * and the group so the switch is understandable.
 */
const EVENT_ROWS: {
    key: DeliveryNotificationEvent;
    label: string;
    trigger: string;
    recipients: string;
}[] = [
    {
        key: 'submitted',
        label: 'Gear list submitted for approval',
        trigger: 'When a gear list moves from Draft to Awaiting approval',
        recipients: 'Supervisor — the project manager',
    },
    {
        key: 'awaiting_approval',
        label: 'Gear list awaiting approval',
        trigger: 'When a gear list moves from Draft to Awaiting approval',
        recipients: 'Transport Manager',
    },
    {
        key: 'approved',
        label: 'Gear list approved',
        trigger: 'When a gear list is approved',
        recipients: 'Supervisor — the project manager',
    },
    {
        key: 'assigned',
        label: 'Pick sheet ready for pickers',
        trigger: 'When a gear list is assigned to the pick crew',
        recipients: 'The pickers on the sheet',
    },
    {
        key: 'pending_photos',
        label: 'Delivery awaiting photos',
        trigger: "When a delivery moves from In Progress to Add Photos",
        recipients: 'Transport Manager',
    },
    {
        key: 'completed',
        label: 'Delivery completed',
        trigger: 'When a delivery is completed',
        recipients: 'Supervisor and Transport Manager',
    },
];

const EMPTY_EVENTS: Record<DeliveryNotificationEvent, boolean> = {
    submitted: true,
    awaiting_approval: true,
    approved: true,
    assigned: true,
    pending_photos: true,
    completed: true,
};

/** A labelled entry for an id already saved but no longer offered — an archived user. */
const withSavedFallback = (live: PickerOption[], savedIds: string[]) => {
    const known = new Set(live.map((option) => option.value));
    const extra = savedIds
        .filter((id) => !known.has(id))
        .map((id) => ({ value: id, label: `User ${id} (unavailable)` }));
    return [...live, ...extra];
};

/**
 * Delivery notification settings (STOCK.md §4). Which of the six workflow notifications
 * fire, and the extra names each recipient group carries on top of the automatic ones.
 */
export default function DeliverySettingsPage() {
    const ability = useAbility(AbilityContext);
    const canRead = ability.can(ACTIONS.READ, RESOURCES.STOCK_DELIVERY_SETTINGS);
    const canUpdate = ability.can(ACTIONS.UPDATE, RESOURCES.STOCK_DELIVERY_SETTINGS);

    const [settings, setSettings] = useState<DeliveryNotificationSettings | null>(null);
    const [events, setEvents] = useState<Record<DeliveryNotificationEvent, boolean>>(EMPTY_EVENTS);
    const [supervisorExtras, setSupervisorExtras] = useState<string[]>([]);
    const [transportExtras, setTransportExtras] = useState<string[]>([]);
    const [userChoices, setUserChoices] = useState<PickerOption[]>([]);
    const [loading, setLoading] = useState(true);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    const names = useMemo(() => {
        const map = new Map<string, string>();
        userChoices.forEach((option) => map.set(option.value, option.label));
        return map;
    }, [userChoices]);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const [resp, usersResp] = await Promise.all([
                deliverySettingsApi.get(),
                // Tolerant: the switches still render if the user picker's list fails,
                // but the extras list would then show bare ids.
                api.get<{ success: boolean; users: AllUser[] }>('/api/users?all_users=true')
                    .catch(() => ({ success: false, users: [] as AllUser[] })),
            ]);

            const loaded = resp.settings;
            setSettings(loaded);
            setEvents({ ...EMPTY_EVENTS, ...loaded.events });
            setSupervisorExtras(loaded.additionalRecipients?.supervisor ?? []);
            setTransportExtras(loaded.additionalRecipients?.transport_manager ?? []);

            const choices: PickerOption[] = (usersResp.users || []).map((user) => ({
                label: user.name,
                value: String(user.id),
                subtitle: user.email,
            }));
            setUserChoices(withSavedFallback(choices, [
                ...(loaded.additionalRecipients?.supervisor ?? []),
                ...(loaded.additionalRecipients?.transport_manager ?? []),
            ]));
            setLoadError(null);
        } catch (err) {
            setLoadError(err instanceof Error && err.message
                ? err.message
                : 'Failed to load delivery notification settings');
        } finally {
            setLoading(false);
            setHasLoaded(true);
        }
    }, []);

    useEffect(() => { if (canRead) load(); }, [canRead, load]);

    const save = async () => {
        try {
            setSaving(true);
            setSaveError(null);
            await deliverySettingsApi.update({
                events,
                additionalRecipients: {
                    supervisor: supervisorExtras,
                    transport_manager: transportExtras,
                },
            });
            toast.success('Delivery notification settings saved');
            await load();
        } catch (err) {
            setSaveError(err instanceof Error && err.message
                ? err.message
                : 'Could not save the delivery notification settings');
        } finally {
            setSaving(false);
        }
    };

    const renderExtras = (
        title: string,
        hint: string,
        values: string[],
        setValues: (next: string[]) => void,
    ) => (
        <div className="mt-4">
            <h3 className="text-sm font-medium">{title}</h3>
            <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>

            {values.length > 0 && (
                <ul className="mt-3 divide-y divide-border rounded-md border border-border">
                    {values.map((id) => (
                        <li key={id} className="flex items-center justify-between gap-3 px-3 py-2">
                            <span className="min-w-0 truncate text-sm">
                                {names.get(id) ?? `User ${id}`}
                            </span>
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                disabled={!canUpdate}
                                aria-label={`Remove ${names.get(id) ?? `user ${id}`}`}
                                onClick={() => {
                                    setValues(values.filter((entry) => entry !== id));
                                    setSaveError(null);
                                }}
                            >
                                <Trash2 size={16} />
                            </Button>
                        </li>
                    ))}
                </ul>
            )}

            {canUpdate && (
                <div className="mt-3">
                    <Combobox
                        options={userChoices.filter((option) => !values.includes(option.value))}
                        value={undefined}
                        onChange={(value) => {
                            if (!value || values.includes(value)) return;
                            setValues([...values, value]);
                            setSaveError(null);
                        }}
                        placeholder="Add a person"
                        searchPlaceholder="Search people…"
                        emptyMessage="No user matches that search."
                        className="w-full md:w-80"
                    />
                </div>
            )}
        </div>
    );

    if (!canRead) return <AccessRestricted />;

    if (loading && !hasLoaded) {
        return (
            <div className="space-y-4 animate-pulse max-w-3xl mx-auto px-4 sm:px-6 py-8">
                {[0, 1].map((i) => <div key={i} className="h-48 bg-gray-300 rounded-lg" />)}
            </div>
        );
    }

    if (loadError && !settings) {
        return (
            <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
                <Card className="border-dashed">
                    <CardContent className="p-8 text-center">
                        <AlertTriangle size={28} className="mx-auto text-destructive mb-3" />
                        <p className="font-medium">Could not load delivery notification settings</p>
                        <p className="text-sm text-muted-foreground mt-1">{loadError}</p>
                        <Button variant="outline" className="mt-4" onClick={load}>Try again</Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-6">
            <div>
                <h1 className="text-2xl font-bold">Delivery notification settings</h1>
                <p className="text-sm text-muted-foreground mt-1">
                    Who gets told as a gear list moves through approval and picking.
                </p>
                {!canUpdate && (
                    <p className="text-sm text-amber-700 mt-2">
                        You do not have permission to change these settings — this page is read-only.
                    </p>
                )}
                {loadError && settings && (
                    <p className="text-sm text-amber-700 mt-2">
                        Showing the last settings we loaded — {loadError}
                    </p>
                )}
            </div>

            <div className="flex gap-2 rounded-md border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900">
                <Info size={16} className="mt-0.5 shrink-0" />
                <p>
                    Recipients are worked out from the delivery itself: the{' '}
                    <strong>Supervisor</strong> is the project manager of the delivery's project,
                    and each <strong>Transport Manager</strong> is anyone whose user role or job
                    role is "Transport Manager". Pickers are the crew assigned to the sheet. Add
                    extra names below only if someone else should also be told.
                </p>
            </div>

            <Card>
                <CardContent className="p-4 md:p-6">
                    <h2 className="font-semibold">Notifications</h2>
                    <p className="text-sm text-muted-foreground mt-1">
                        Each switch controls one email and in-app notification. Turning one off
                        stops both channels for that step.
                    </p>

                    <div className="mt-4 divide-y divide-border">
                        {EVENT_ROWS.map((row) => (
                            <div
                                key={row.key}
                                className="flex flex-col gap-3 py-4 md:flex-row md:items-center md:gap-6"
                            >
                                <div className="min-w-0 md:flex-1">
                                    <p className="text-sm font-medium">{row.label}</p>
                                    <p className="text-xs text-muted-foreground">{row.trigger}</p>
                                    <p className="text-xs text-muted-foreground mt-0.5">
                                        Goes to: {row.recipients}
                                    </p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Switch
                                        checked={events[row.key]}
                                        disabled={!canUpdate}
                                        aria-label={`${row.label} notification enabled`}
                                        onCheckedChange={(checked) => {
                                            setEvents((current) => ({ ...current, [row.key]: checked }));
                                            setSaveError(null);
                                        }}
                                    />
                                    <span className="text-xs text-muted-foreground">
                                        {events[row.key] ? 'On' : 'Off'}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="mt-6 border-t border-border pt-4">
                        <h2 className="font-semibold">Additional recipients</h2>
                        <p className="text-sm text-muted-foreground mt-1">
                            Optional. These names are added to the group on top of the automatic
                            recipients above.
                        </p>

                        {renderExtras(
                            'Supervisors',
                            'Added alongside the project manager on the submitted, approved and completed notifications.',
                            supervisorExtras,
                            setSupervisorExtras,
                        )}
                        {renderExtras(
                            'Transport Managers',
                            'Added alongside every user holding the Transport Manager role on the awaiting-approval, photos and completed notifications.',
                            transportExtras,
                            setTransportExtras,
                        )}
                    </div>

                    {saveError && <p className="mt-4 text-sm text-destructive">{saveError}</p>}

                    <div className="mt-6 flex flex-col md:flex-row md:justify-end">
                        <Button onClick={save} disabled={!canUpdate || saving} className="w-full md:w-auto">
                            {saving ? 'Saving…' : 'Save changes'}
                        </Button>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
